import React, { Fragment } from 'react';
import { ArrowDownIcon, CheckIcon, PackageIcon, SparklesIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { AnimatedMetric } from './AnimatedMetric';
import { ResearchSource } from './ResearchSource';
const comparisons = [{
  title: 'ADVERTISING',
  points: ['Targeted', 'Automated', 'Scalable', 'Measured'],
  tone: 'border-black/15'
}, {
  title: 'TRADITIONAL SAMPLING',
  points: ['Manual', 'Location dependent', 'Hard to follow up', 'Disconnected from media'],
  tone: 'border-black/15'
}, {
  title: 'SAMPLER',
  points: ['Targeted', 'Interactive', 'Scalable', 'Measurable'],
  tone: 'border-[#ff4d17] bg-[#ff4d17] text-white'
}];
const attentionStats = [{
  value: 52,
  label: 'said advertising disrupts the experience.',
  ariaLabel: '52 percent said advertising disrupts the experience'
}, {
  value: 49,
  label: 'said there is too much advertising.',
  ariaLabel: '49 percent said there is too much advertising'
}, {
  value: 44,
  label: 'said advertising is annoying.',
  ariaLabel: '44 percent said advertising is annoying'
}];
const stages = [['01', 'CREATE', 'Build the campaign. Define product, place and mechanics.'], ['02', 'EXPERIENCE', 'Give people something they want to join.'], ['03', 'DISTRIBUTE', 'Connect digital discovery to a real product.'], ['04', 'MEASURE', 'Follow catch, redemption and feedback.']];
const system = ['BRAND', 'SAMPLER', 'INTERACTIVE EXPERIENCE', 'PRODUCT CAUGHT', 'REDEMPTION', 'PRODUCT EXPERIENCE', 'FEEDBACK', 'CAMPAIGN INSIGHTS'];
export function AttentionAndSystem() {
  const reduceMotion = useReducedMotion();
  return <div id="platform" data-id="element-10">
      <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="attention-heading" data-id="element-11">
        <div className="mx-auto max-w-[1280px]" data-id="element-12">
          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.65
        }} transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-xs font-extrabold tracking-[0.16em] text-black/55" data-id="element-13">
            THE ATTENTION PROBLEM / RESEARCH NOTE 01
          </motion.p>
          <motion.h2 id="attention-heading" initial={reduceMotion ? false : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.5
        }} transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-5 max-w-5xl font-display text-5xl font-bold leading-[0.9] md:text-8xl" data-id="element-14">
            People don’t want more advertising.
            <br data-id="element-15" />
            <span className="text-[#ff4d17]" data-id="element-16">
              They want something worth engaging with.
            </span>
          </motion.h2>

          <div className="mt-16 grid gap-8 border-y border-black/15 py-8 md:grid-cols-3" data-id="element-17">
            {attentionStats.map((stat, index) => <motion.article key={stat.value} initial={reduceMotion ? false : {
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.75
          }} transition={{
            delay: reduceMotion ? 0 : index * 0.07,
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-18">
                <AnimatedMetric value={stat.value} suffix="%" ariaLabel={stat.ariaLabel} className="font-display text-7xl font-bold text-[#ff4d17]" data-id="element-19" />
                <p className="mt-4 max-w-xs text-base leading-relaxed text-black/75" data-id="element-20">
                  {stat.label}
                </p>
              </motion.article>)}
          </div>

          <ResearchSource sourceId="SR-01" status="verified" title="Sveriges Annonsörer + Novus — advertising fatigue in Sweden" summary="The three percentages above are reproduced from this published Swedish advertising-attitudes summary: disruption of the experience (52%), too much advertising (49%), and advertising is annoying (44%)." methodology="Published 25 Aug 2022. The page describes an annual study of Swedes’ attitudes to advertising. It is a 2022 public research reference; use it as context for the category tension, not as a current universal consumer benchmark." published="25 AUG 2022" sourceUrl="https://www.sverigesannonsorer.se/aktuellt/efter-trendbrottet-under-pandemin/" sourceLabel="READ THE PUBLISHED SOURCE" className="mt-8" data-id="element-21" />

          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          x: -18
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.7
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-16 font-display text-4xl font-bold md:text-6xl" data-id="element-22">
            So we stopped making ads.
          </motion.p>
          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          x: -18
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.7
        }} transition={{
          delay: reduceMotion ? 0 : 0.08,
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-3 font-display text-4xl font-bold text-[#ff4d17] md:text-6xl" data-id="element-23">
            And started making experiences.
          </motion.p>
        </div>
      </section>

      <section className="border-y border-black/15 bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="gap-heading" data-id="element-24">
        <div className="mx-auto max-w-[1280px]" data-id="element-25">
          <p className="text-xs font-extrabold tracking-[0.16em] text-black/55" data-id="element-26">
            THE GAP
          </p>
          <h2 id="gap-heading" className="mt-5 max-w-5xl font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-27">
            Media learned how to target, distribute and measure at scale.
            <br data-id="element-28" />
            <span className="text-black/45" data-id="element-29">Product experience didn’t.</span>
          </h2>
          <div className="mt-14 grid gap-4 lg:grid-cols-3" data-id="element-30">
            {comparisons.map((column, index) => <motion.article key={column.title} initial={reduceMotion ? false : {
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            delay: reduceMotion ? 0 : index * 0.08,
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} className={`min-h-[300px] border p-7 md:p-9 ${column.tone}`} data-id="element-31">
                <p className="text-xs font-extrabold tracking-[0.14em] opacity-65" data-id="element-32">
                  {column.title}
                </p>
                <ul className="mt-12 space-y-5 text-xl font-bold" data-id="element-33">
                  {column.points.map(point => <li key={point} className="flex items-center gap-3" data-id="element-34">
                      <CheckIcon size={17} aria-hidden="true" data-id="element-35" />
                      {point}
                    </li>)}
                </ul>
              </motion.article>)}
          </div>
          <p className="mt-14 max-w-3xl font-display text-3xl font-bold leading-tight md:text-5xl" data-id="element-36">
            We are building the distribution and measurement layer for product
            experience.
          </p>
        </div>
      </section>

      <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="meet-heading" data-id="element-37">
        <div className="mx-auto max-w-[1280px]" data-id="element-38">
          <div className="grid gap-8 md:grid-cols-[1fr_0.8fr] md:items-end" data-id="element-39">
            <div data-id="element-40">
              <p className="text-xs font-extrabold tracking-[0.16em] text-black/55" data-id="element-41">
                MEET SAMPLER
              </p>
              <h2 id="meet-heading" className="mt-5 font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-42">
                A Brand New Way of Sampling.
              </h2>
            </div>
            <p className="max-w-xl text-lg leading-relaxed text-black/75" data-id="element-43">
              Create, distribute and measure real product experiences. Not
              handouts. Connected campaigns designed around audience, location
              and experience.
            </p>
          </div>
          <div className="mt-16 grid gap-px overflow-hidden border border-black/15 bg-black/15 md:grid-cols-4" data-id="element-44">
            {stages.map(([number, title, copy], index) => <motion.article key={title} initial={reduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.45
          }} transition={{
            delay: reduceMotion ? 0 : index * 0.06,
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="min-h-[230px] bg-[#f7f5ef] p-6" data-id="element-45">
                <p className="text-xs font-bold text-[#ff4d17]" data-id="element-46">{number}</p>
                <h3 className="mt-10 font-display text-3xl font-bold" data-id="element-47">
                  {title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-black/70" data-id="element-48">
                  {copy}
                </p>
              </motion.article>)}
          </div>
          <a href="#start" className="mt-10 inline-flex rounded-full bg-black px-6 py-3.5 text-sm font-bold text-white hover:bg-black/80" data-id="element-49">
            BUILD YOUR FIRST CAMPAIGN
          </a>
        </div>
      </section>

      <section className="bg-black px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="system-heading" data-id="element-50">
        <div className="mx-auto max-w-[1280px]" data-id="element-51">
          <p className="text-xs font-extrabold tracking-[0.16em] text-white/55" data-id="element-52">
            ONE CONNECTED PLATFORM
          </p>
          <h2 id="system-heading" className="mt-5 font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-53">
            From product to person.
          </h2>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-white/75" data-id="element-54">
            One campaign. One flow. One place to follow what happens.
          </p>
          <div className="relative mt-16 grid gap-2 md:grid-cols-4 lg:grid-cols-8" data-id="element-55">
            {system.map((stage, index) => <Fragment key={stage} data-id="element-56">
                <motion.div initial={reduceMotion ? false : {
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: reduceMotion ? 0 : index * 0.05,
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1]
            }} className={`relative min-h-[120px] border p-4 ${stage === 'SAMPLER' ? 'border-[#ff4d17] bg-[#ff4d17] text-black' : 'border-white/20'}`} data-id="element-57">
                  <p className="text-[9px] font-extrabold tracking-[0.12em] opacity-60" data-id="element-58">
                    0{index + 1}
                  </p>
                  <p className="mt-7 text-sm font-bold leading-tight" data-id="element-59">
                    {stage}
                  </p>
                  {index === 3 && <motion.span animate={reduceMotion ? undefined : {
                x: [0, 36, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -bottom-3 left-3 grid h-6 w-6 place-items-center rounded-full bg-[#ff4d17] text-black" data-id="element-60">
                      <PackageIcon size={13} aria-hidden="true" data-id="element-61" />
                    </motion.span>}
                </motion.div>
                {index < system.length - 1 && <ArrowDownIcon className="mx-auto h-5 w-5 text-white/50 md:hidden" aria-hidden="true" data-id="element-62" />}
              </Fragment>)}
          </div>
          <div className="mt-12 flex items-start gap-3 text-white/75" data-id="element-63">
            <SparklesIcon size={18} className="mt-0.5 text-[#ff4d17]" aria-hidden="true" data-id="element-64" />
            <p data-id="element-65">
              Sampler connects the digital campaign with the physical product
              experience.
            </p>
          </div>
        </div>
      </section>
    </div>;
}